/**
 * ComplementaryItemsReport.jsx — R5
 * Route: /pos/reports/complementary-items
 */
import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Popover, Spin, Tag } from "antd";
import { Download, Filter, Calendar } from "react-feather";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import SelectComponent from "react-select";
import PrintReportPreview from "../../reports/PrintReportPreview";

const getToken    = () => localStorage.getItem("token");
const getApiUrl = () => {
  const base = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");
  return base ;//.endsWith("/pos") ? base : `${base}/pos`;
};
const getCurrency = () => localStorage.getItem("currencycode") || "₹";
const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];
const getBizDate = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const d = localStorage.getItem(key);
    if (d && moment(d).isValid()) return moment(d).startOf("day");
  }
  return getBizDate().clone().startOf("day");
};
const pillStyle   = (bg,color)=>({background:bg,color,fontWeight:800,fontSize:12,padding:"5px 10px",borderRadius:999,border:`1px solid ${color}33`,whiteSpace:"nowrap"});
const downloadCSV = (columns,data,filename)=>{
  const h=columns.filter(c=>c.dataIndex).map(c=>c.title).join(",");
  const r=data.map(row=>columns.filter(c=>c.dataIndex).map(c=>`"${String(row[c.dataIndex]??"").replace(/"/g,'""')}"`).join(","));
  const blob=new Blob([[h,...r].join("\n")],{type:"text/csv;charset=utf-8;"}); const url=URL.createObjectURL(blob);
  const a=document.createElement("a"); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
};

const ComplementaryItemsReport = () => {
  const currency=getCurrency(); const apiURL=getApiUrl();
  const [dateRange,     setDateRange]     = useState({startDate:getBizDate(),endDate:getBizDate()});
  const [loading,       setLoading]       = useState(false);
  const [items,         setItems]         = useState([]);
  const [grandTotal,    setGrandTotal]    = useState({total_qty:0,estimated_cost:0});
  const [previewVisible,setPreviewVisible]= useState(false);
  const [exportData,    setExportData]    = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [sourceFilter,  setSourceFilter]  = useState({value:"both",label:"Both (KOT + Order)"});

  const sourceOptions=[{value:"both",label:"Both (KOT + Order)"},{value:"kot",label:"KOT only"},{value:"order",label:"Order only"}];

  const fetchReport=useCallback(async()=>{
    setLoading(true);
    try{
      const res=await axios.get(`${apiURL}/pos/complementary-items`,{
        headers:{Authorization:`Bearer ${getToken()}`},
        params:{ start_date:dateRange.startDate.format("YYYY-MM-DD"), end_date:dateRange.endDate.format("YYYY-MM-DD"), source:sourceFilter?.value||"both" }
      });
      if(res.data.success){setItems(res.data.items||[]);setGrandTotal(res.data.grandTotal||{});}
    }catch(err){Swal.fire({icon:"error",title:"Error",text:err?.response?.data?.error||err.message});}
    finally{setLoading(false);}
  },[dateRange,sourceFilter,apiURL]);

  useEffect(()=>{fetchReport();},[fetchReport]);

  const columns=[
    {title:"Item Name",             dataIndex:"item_name",      width:200,fixed:"left"},
    {title:"Source",                dataIndex:"source",         width:90, render:v=><Tag color={v==="KOT"?"purple":"geekblue"}>{v}</Tag>},
    {title:"Date",                  dataIndex:"business_date",  width:120},
    {title:"Table/Room",            dataIndex:"table_room",     width:110},
    {title:"Qty Comped",            dataIndex:"total_qty",      width:100,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Unit Price (${currency})`,dataIndex:"unit_price",   width:120,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Est. Cost (${currency})`, dataIndex:"estimated_cost",width:130,align:"right",fixed:"right",
     render:v=><strong style={{color:"#dc2626"}}>{Number(v||0).toFixed(2)}</strong>},
    {title:"KOTs/Orders",           dataIndex:"kot_count",      width:110,align:"right"},
    {title:"Waiter",                dataIndex:"waiter_name",    width:110,render:v=>v||<span style={{color:"#94a3b8"}}>—</span>},
    {title:"Raised By (User ID)",   dataIndex:"raised_by",      width:140,render:v=>v||<span style={{color:"#94a3b8"}}>—</span>},
  ];

  const summaryRow=()=>(
    <Table.Summary.Row style={{background:"#fef2f2",fontWeight:700}}>
      <Table.Summary.Cell index={0} colSpan={4}>TOTAL</Table.Summary.Cell>
      <Table.Summary.Cell align="right">{Number(grandTotal.total_qty||0).toFixed(2)}</Table.Summary.Cell>
      <Table.Summary.Cell/>
      <Table.Summary.Cell align="right">{currency} {Number(grandTotal.estimated_cost||0).toFixed(2)}</Table.Summary.Cell>
      <Table.Summary.Cell colSpan={3}/>
    </Table.Summary.Row>
  );

  return(
    <div className="page-wrapper"><div className="content container-fluid">
      <div className="row align-items-center mb-3">
        <div className="col-auto d-flex align-items-center gap-3">
          <h2 style={{fontSize:20,fontWeight:700,borderBottom:"2px solid #007bff",paddingBottom:4,margin:0}}>Complementary Items Report</h2>
          <DateRangePicker
            key={`${dateRange.startDate.format("YYYY-MM-DD")}-${dateRange.endDate.format("YYYY-MM-DD")}`}
            initialSettings={{startDate:dateRange.startDate.toDate(),endDate:dateRange.endDate.toDate(),
              ranges:{Today:[getBizDate().clone().startOf("day").toDate(),getBizDate().clone().endOf("day").toDate()],"This Month":[getBizDate().clone().startOf("month").toDate(),getBizDate().clone().endOf("month").toDate()],
                "Last Month":[getBizDate().clone().subtract(1,"month").startOf("month").toDate(),getBizDate().clone().subtract(1,"month").endOf("month").toDate()]},
              locale:{format:"DD MMM YYYY",applyLabel:"Apply",cancelLabel:"Cancel"},alwaysShowCalendars:true,autoApply:false}}
            onApply={(_,p)=>setDateRange({startDate:p.startDate,endDate:p.endDate})}>
            <div className="input-group" style={{width:250}}>
              <input type="text" className="form-control text-center fw-bold" style={{height:38,fontSize:14,borderRight:"none",background:"#fff"}}
                value={`${dateRange.startDate.format("DD MMM YYYY")} – ${dateRange.endDate.format("DD MMM YYYY")}`} readOnly/>
              <span className="input-group-text" style={{background:"#fff",borderLeft:"none",height:38,cursor:"pointer"}}><Calendar size={15} color="#495057"/></span>
            </div>
          </DateRangePicker>
        </div>
        <div className="col-auto ms-auto d-flex align-items-center gap-2">
          {grandTotal.estimated_cost>0&&<span style={pillStyle("#fef2f2","#dc2626")}>{currency} {Number(grandTotal.estimated_cost||0).toFixed(2)} Waived</span>}
          <div style={{width:210}}>
            <SelectComponent options={sourceOptions} value={sourceFilter} onChange={setSourceFilter} styles={{control:b=>({...b,fontSize:13,height:38})}}/>
          </div>
          <div className="dropdown">
            <Button type="primary" className="d-flex align-items-center dropdown-toggle" data-bs-toggle="dropdown" style={{height:38}}>
              <Download size={15} className="me-2"/>Download
            </Button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><button className="dropdown-item" onClick={()=>downloadCSV(columns,items,`Comp_Items_${dateRange.startDate.format("YYYYMMDD")}.csv`)}>Download CSV</button></li>
              <li><button className="dropdown-item" onClick={()=>{setExportData(items);setExportHeaders(columns.filter(c=>c.dataIndex).map(c=>({label:c.title,key:c.dataIndex})));setPreviewVisible(true);}}>Print / PDF Preview</button></li>
            </ul>
          </div>
        </div>
      </div>
      {loading?<div className="text-center my-5"><Spin size="large"/></div>:(
        <Table columns={columns} dataSource={items} rowKey={(_,i)=>i}
          scroll={{x:"max-content"}} size="small" bordered
          pagination={{pageSize:50,showTotal:t=>`${t} line items`}}
          summary={items.length>0?summaryRow:undefined}/>
      )}
      <PrintReportPreview visible={previewVisible} onClose={()=>setPreviewVisible(false)}
        reportTitle={`Complementary Items — ${dateRange.startDate.format("DD MMM YYYY")} to ${dateRange.endDate.format("DD MMM YYYY")}`}
        propertyName="" data={exportData} headers={exportHeaders} footerData="" totalColumns={[]} apiEndpoint="transactions/generate-pdf" apiUrl={apiURL}/>
    </div></div>
  );
};

export default ComplementaryItemsReport;
